import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, Watch } from "@stencil/core";

let id = 0;

/**
 * @slot (default) - The label.
 */
@Component({
    tag: "uic-radio",
    styleUrl: "radio.scss"
})
export class Radio implements ComponentInterface {
    private inputId = `uic-radio-${++id}`;
    private labelId = `uic-radio-label-${id}`;

    @Element() host: HTMLUicRadioElement;

    /**
     * The text to show next to the checked indicator.
     */
    @Prop() readonly label?: string;

    /**
     * The icon next to the label.
     */
    @Prop() readonly icon?: string;

    /**
     * If set to `true`, the user cannot interact with the component.
     */
    @Prop({ reflect: true }) readonly disabled: boolean = false;

    /**
     * If set to `true`, the radio is considered to be checked.
     */
    @Prop({ reflect: true, mutable: true }) checked = false;

    /**
     * The value of the radio button.
     */
    /* eslint-disable-next-line */
    @Prop({ mutable: true }) value: any;

    /**
     * Emitted when the checked state of the radio changes.
     */
    @Event() uicChange: EventEmitter;

    @Watch("checked")
    checkedChanged() {
        this.uicChange.emit();
    }

    connectedCallback() {
        if (this.value === undefined) {
            this.value = this.inputId;
        }
    }

    private handleClick() {
        this.checked = true;
    }

    render() {
        return (
            <Host
                class={{
                    "uic-radio": true,
                    "uic-radio--checked": this.checked,
                    "uic-radio--disabled": this.disabled
                }}
                role='radio'
                aria-checked={`${this.checked}`}
                aria-hidden={this.disabled ? "true" : null}
                aria-labelledby={this.label ? this.labelId : null}
                tabindex={this.disabled ? "-1" : "0"}
            >
                <label htmlFor={this.inputId} class='uic-radio__control'>
                    <input
                        class='uic-radio__input'
                        type='radio'
                        id={this.inputId}
                        checked={this.checked}
                        disabled={this.disabled}
                        tabIndex={-1}
                        onClick={() => this.handleClick()}
                    />
                    <span class='uic-radio__indicator'></span>
                    <span id={this.labelId} class='uic-radio__label'>
                        {this.icon && <uic-icon class='uic-radio__icon' name={this.icon}></uic-icon>}
                        <slot>{this.label}</slot>
                    </span>
                </label>
            </Host>
        );
    }
}
